import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';

import { DownOutlined } from '@ant-design/icons';
import './navbar.scss';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { eventActions, contentsActions, userActions } from 'src/app/redux/actions';
import { reduxRootTypes } from 'src/types';

// UI Components
import styles from 'src/app/components/Navbar/Navbar.module.scss';
import UserForm from 'src/app/components/Forms/UserForm/userForm';
import LRC from '../LRC/LRC';

// Helpers
import { routeHelper, socketHelper, hymotsESocketHelper, logsSocketHelper } from 'src/app/helpers';

// Config and Types
import { RegisterDataType } from 'src/types/services/user-service.type';
import { urlsConfig } from 'src/configs';
import { supportedLanguages, SupportedLanguagesKey } from 'src/types/redux/user.redux.types';

// Services
import { helpAndSupportService, userService } from 'src/app/services';

// Custom Components and Hooks
import useLogout from 'src/app/hooks/useLogout';
import { closeSocketConnection } from 'src/app/helpers/socket.helper';
import { getPopupContainer } from 'src/app/helpers/getPopupContainer.helper';
import Select from 'antd/es/select';
import Layout from 'antd/es/layout';
import Button from 'antd/es/button';
import Menu from 'antd/es/menu';
import Dropdown from 'antd/es/dropdown/dropdown';

const { Option } = Select;
const { Header } = Layout;

const Navbar = () => {
  const { t } = useTranslation();

  const history = useHistory();
  const dispatch = useDispatch();

  const logout = useLogout();

  const userState = useSelector((state: reduxRootTypes.RootStateType) => state.userState);
  const eventState = useSelector((state: reduxRootTypes.RootStateType) => state.eventState);
  const { eventName, eventClassification, hymotsDB } = useSelector(
    (state: reduxRootTypes.RootStateType) => state.eventState,
  );

  const { version } = useSelector((state: reduxRootTypes.RootStateType) => state.settingsState);

  const [changeProfileModal, setChangeProfileModal] = useState<boolean>(false);
  const [userFormData, setUserFormData] = useState<RegisterDataType | undefined>(undefined);
  const [LRCModal, setLRCModal] = useState<boolean>(false);

  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguagesKey>(
    i18next.language as SupportedLanguagesKey,
  );

  const languages = supportedLanguages.map((key) => ({
    key,
    label: t(`userProfileMenu.languages.${key}`),
  }));

  const showOnlyVersionLanguageAndLogout = !eventState.eventId && userState.isRegistered;

  const onLanguageChange = async (langKey: SupportedLanguagesKey) => {
    // Optimistic update for UI responsiveness
    dispatch(userActions.setPreferredLanguage(langKey));
    setCurrentLanguage(langKey);
    i18next.changeLanguage(langKey);

    try {
      await userService.updateMeLanguage(langKey);
    } catch (error) {
      console.log('error', error);
    }
  };

  const onswitchDatabase = () => {
    dispatch(eventActions.eventIdChanged(''));
    dispatch(contentsActions.lineOfEffortList([]));
    dispatch(
      eventActions.eventDataChanged({
        eventClassification: '',
        eventName: '',
        hymotsDB: 0,
        hlaContent: undefined,
      }),
    );
    const gameId = Number(hymotsDB?.toString());
    const etmenMsg = {
      mesajTipi: 1,
      mesajAltTipi: 37,
      oyunId: gameId,
      icerik: {
        abonelikTipi: 1,
        filterList: [],
      },
    };

    socketHelper.sendMessage(etmenMsg);

    hymotsESocketHelper.closeSocketConnection(Number(hymotsDB?.toString()));

    logsSocketHelper.closeSocket();
  };
  const handleClickLogo = () => {
    routeHelper.handleRoute(history, '/');
  };

  const onChangeProfile = async () => {
    const { data } = await userService.getMe();

    const currentUser = data.user;
    if (!currentUser) return;
    setUserFormData({
      ...currentUser,
      events: [
        ...currentUser.events.map((e) => ({
          ...e,
          roles: [...e.roles.map((r) => r.id)],
        })),
      ],
    });
    setChangeProfileModal(true);
  };

  const onClickLRC = () => {
    setLRCModal(true);
  };

  const handleDownloadMilManual = async () => {
    const { data } = await helpAndSupportService.getUserManualRawMil();

    const blob = new Blob([data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'MILManagementWithEtecube.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <Header className={styles.container}>
        <div>
          <Button onClick={handleClickLogo}>
            <img src="/assets/etecubeh.png" style={{ height: '32px' }} />
          </Button>
        </div>
        <div className={styles.toolbar}>
          <h4 className={styles.unclassified}>{eventClassification ? eventClassification : ''}</h4>
          <div>
            <h4 className={styles.boldEcho}>{eventName}</h4>
          </div>
        </div>

        <div className={styles.dropdown}>
          <Dropdown
            key="more"
            getPopupContainer={getPopupContainer}
            overlay={
              <Menu className={styles.dropdownItems}>
                <Menu.Item className="navbar-menu-item__version">
                  <div className="switch_event">
                    <span>
                      {t('userProfileMenu.version')}: v{version['User Interface']}
                    </span>
                  </div>
                </Menu.Item>
                <Menu.Item id="navbar-language" className="navbar-menu-item">
                  <div onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={currentLanguage}
                      onChange={onLanguageChange}
                      placeholder={t('userProfileMenu.language')}
                      dropdownStyle={{ zIndex: 100001 }}
                      getPopupContainer={() => document.body}
                    >
                      {languages.map((lang) => (
                        <Option key={lang.key} value={lang.key}>
                          {lang.label}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Menu.Item>
                {!showOnlyVersionLanguageAndLogout && (
                  <Menu.Item className="navbar-menu-item" onClick={onChangeProfile}>
                    <div className="switch_event">
                      <span>
                        {userState.isLocalUser ? t('userProfileMenu.editProfile') : t('userProfileMenu.profile')}
                      </span>
                    </div>
                  </Menu.Item>
                )}
                {!showOnlyVersionLanguageAndLogout && (
                  <Menu.Item className="navbar-menu-item" onClick={onswitchDatabase}>
                    <div className="switch_event">
                      <span>{t('userProfileMenu.switchDatabase')}</span>
                    </div>
                  </Menu.Item>
                )}
                {!showOnlyVersionLanguageAndLogout && (
                  <Menu.Item className="navbar-menu-item" onClick={onClickLRC}>
                    <div className="switch_event">
                      <span>LRC</span>
                    </div>
                  </Menu.Item>
                )}
                {!showOnlyVersionLanguageAndLogout && (
                  <Menu.Item className="navbar-menu-item" key="help">
                    <Dropdown
                      trigger={['hover']}
                      placement="bottomRight"
                      overlay={
                        <Menu mode="inline">
                          <Menu.Item
                            className="navbar-menu-item"
                            onClick={() => {
                              routeHelper.handleRoute(history, '/HelpAndSupport');
                            }}
                          >
                            <div className="switch_event">
                              <span>{t('userProfileMenu.userManual')}</span>
                            </div>
                          </Menu.Item>
                          <Menu.Item className="navbar-menu-item" onClick={handleDownloadMilManual}>
                            <div className="switch_event">
                              <span>{t('userProfileMenu.milManual')}</span>
                            </div>
                          </Menu.Item>
                        </Menu>
                      }
                    >
                      <div className="switch_event" onClick={(e) => e.stopPropagation()}>
                        <span>{t('userProfileMenu.helpAndSupport')}</span>
                      </div>
                    </Dropdown>
                  </Menu.Item>
                )}
                {!showOnlyVersionLanguageAndLogout && (
                  <Menu.Item
                    className="navbar-menu-item"
                    onClick={() => {
                      routeHelper.handleRoute(history, '/ProductLicenses');
                    }}
                  >
                    <div className="switch_event">
                      <span>{t('general.licenses')}</span>
                    </div>
                  </Menu.Item>
                )}
                <Menu.Item
                  className="navbar-menu-item"
                  onClick={() => {
                    logout();
                    closeSocketConnection(Number(hymotsDB?.toString()));
                  }}
                >
                  <div className="logout_div">
                    <span>{t('userProfileMenu.logout')}</span>
                  </div>
                </Menu.Item>
              </Menu>
            }
          >
            <Button className="navbar-menu-button">
              <div className={styles.dropdownButton}>
                <img className={styles.navbarUserIcon} src="/assets/userIcon.png" />
                <span className={styles.usernameSpan}>{userState.userName?.toUpperCase()}</span>
                <DownOutlined
                  style={{
                    fontSize: 14,
                    marginLeft: 4,
                    color: 'white',
                    width: 5,
                  }}
                />
              </div>
            </Button>
          </Dropdown>
        </div>
      </Header>
      {changeProfileModal && (
        <UserForm
          setEditModal={false}
          onClickOpen={changeProfileModal}
          setOnClickOpen={setChangeProfileModal}
          userForm={userFormData}
          editProfile
        />
      )}
      {LRCModal && <LRC onClickOpen={LRCModal} setOnClickOpen={setLRCModal} />}
    </>
  );
};

export default Navbar;
